import { computed, h, onScopeDispose, ref } from 'vue'

import { enumMetaFor } from '@/lib/enumMeta.js'
import { labelFor } from '@/lib/fieldLabels.js'
import { FIELD_META } from '@/lib/fieldMeta.js'

/** The fields every collection stamps as ISO 8601 text. */
const TIMESTAMPS = ['createdAt', 'updatedAt']

/**
 * What a PATCH accepts, per noun.
 *
 * Not derived from the surface: what is searchable and what is editable are
 * different questions, and this server answers the second with
 * `additionalProperties: false` on every body. `createdAt` is searchable and is
 * the platform's to stamp; `operationInfo` is searchable and is changed through
 * its own route. Sending the whole record — which is what the panel hands over
 * — would be a 400 naming the first field it did not want.
 */
const EDITABLE = {
    'companies': ['name', 'website'],
    'auction-operations': ['name'],
    'dealership-operations': ['name'],
    'auction-houses': ['name', 'slug'],
    'dealerships': ['name']
}

/**
 * Which editable fields a PATCH will take `null` for.
 *
 * Per field rather than blanket, because the routes are. A Company's `website`
 * is declared `anyOf: [uri, null]` so it can be cleared; `name` is
 * `minLength: 1` and a house's `slug` is `^[a-z0-9]{4}$`, and neither takes a
 * null any more than it takes an empty string. So an emptied box means "clear
 * it" only here — everywhere else it stays the refusal it is, which the panel
 * now reports.
 */
const CLEARABLE = {
    'companies': ['website']
}

/**
 * The nested properties a PATCH also accepts, drawn as a group rather than a
 * box.
 *
 * They are objects — a Company's billing facts, a house's address — so neither
 * the field controls nor the surface can offer them as one control. What the
 * surface *does* carry is every leaf beneath them, as dotted paths, so the
 * group is built from those and reassembled on the way out.
 *
 * Both take `null` to clear, which is the one thing a body distinguishes from
 * an absent key. A house going digital-only clears its location; that is
 * stress-test scenario 5.
 */
//`addressAt` names where a postal address sits inside the group, and its
//presence is what makes the group draw an address editor rather than a row of
//generic controls. `''` means the group *is* the address; a path means the
//address is one property of a larger object, and the rest of that object keeps
//its generic controls.
//
//`geocode` says whether a point is kept with it. A Company's billing address
//declares no `geo` — billing needs a deliverable address, not coordinates — so
//a point written there would be a refused save.
const EDITABLE_OBJECTS = {
    'companies': [{ key: 'billing', label: 'Billing facts', addressAt: 'address', geocode: false }],
    'auction-houses': [{ key: 'location', label: 'Location', addressAt: '', geocode: true }],
    'dealerships': [{ key: 'location', label: 'Location', addressAt: '', geocode: true }]
}

/**
 * The columns a collection's table opens with, per noun. Every other published
 * field can still be turned on from the Options menu.
 *
 * A house and a dealership are found by where they are and whose they are; an
 * Operation by the company behind it; a Company by its site.
 */
const TABLE_COLUMNS = {
    'companies': ['__primary', 'website', 'status', 'updatedAt'],
    'auction-operations': ['__primary', 'legalCompany.name', 'status', 'updatedAt'],
    'dealership-operations': ['__primary', 'legalCompany.name', 'status', 'updatedAt'],
    'auction-houses': ['__primary', 'operationInfo.name', 'location.city', 'status', 'updatedAt'],
    'dealerships': ['__primary', 'operationInfo.name', 'location.city', 'status', 'updatedAt']
}

/**
 * What a POST needs beyond a name, per noun.
 *
 * `operationId` is not a property of the document — a stored house carries
 * `operationInfo`, which the server denormalizes — so it appears nowhere in the
 * searchable surface and the panel's own form cannot draw a control for it. The
 * instance body asks for it instead, and only while creating.
 */
const NEEDS_OPERATION = {
    'auction-houses': 'auctionOperations',
    'dealerships': 'dealershipOperations'
}

/**
 * How many rows one read asks for.
 *
 * Named rather than left to the server's default, and for two reasons.
 * `<AdminCollectionLayout>` renders whatever `config.data` holds and draws no
 * paginator of its own, so a screen that never asked for a second page showed
 * one page of a collection and offered no way to the rest. And the store counts
 * pages only once somebody names a size — `pageCount` is null until then — so
 * without this there is no total for a paginator to divide.
 */
const PAGE_SIZE = 50

/**
 * The backing store for <AdminCollectionLayout>, over the SDK.
 *
 * This is vibe-portal's `useFqlCollection` role, played against a different
 * layer. That composable owns a fetch client, a debounce and a request-id race
 * guard of its own; here the SDK store already owns the transport and the
 * failure handling, so what is left is the shape conversion the component
 * expects — a `config`, a `valueProvider` and a `facetProvider`.
 *
 * **The component owns the query; the server answers it.** `@update:query`
 * hands over the FQL string the user typed, debounced here because the
 * component fires on every keystroke and each one is a request. The component
 * then re-applies the same query client-side with its own engine, which is
 * idempotent against a server that already applied it.
 *
 * @param {Object} config
 * @param {Object} config.store   One of the SDK's five collection stores.
 * @param {string} config.collection The route id — which noun this is.
 * @param {string} config.title   What the screen calls this collection.
 * @param {string} config.singular One of them, for naming a new record.
 * @param {string} config.storageKey Where the component persists its view state.
 */
export function useAdministrationCollection({ store, collection, title, singular, storageKey, debounceMs = 250 }) {
    const query = ref('')
    // The order the server is asked for, as `{ field, direction }`, or null for
    // its own (by name).
    const sort = ref(null)
    const page = ref(1)
    const readFailure = ref(null)
    const writeFailure = ref(null)
    // Whether a page of rows has come back yet, whether it held rows or a
    // refusal. Until it has, an empty list is not an answer.
    const arrived = ref(false)
    let timer = null

    /**
     * Every read this screen makes, and the one place a refused read is held.
     *
     * `store.error` catches writes too, and every write on this screen already
     * reports beside the control that made it. A banner reading `store.error`
     * said the same refusal twice, the second time far from what it was about.
     */
    async function read(work) {
        const ok = await work()
        readFailure.value = ok === false ? store.error : null
        return ok
    }

    async function runSearch(nextPage) {
        page.value = nextPage
        const ok = await read(() => store.search({
            query: query.value,
            page: nextPage,
            pageSize: PAGE_SIZE,
            sort: sort.value
        }))
        arrived.value = true
        return ok
    }

    // Back to the first page. Page four of one query or order is not page four
    // of the next, and a server asked for it answers rows nobody searched for.
    // The query and the sort share one timer, so the two the layout sends as it
    // mounts become one request.
    function schedule() {
        clearTimeout(timer)
        timer = setTimeout(() => runSearch(1), debounceMs)
    }

    function onQuery(next) {
        query.value = next ?? ''
        schedule()
    }

    /**
     * What a column's sort control calls. The layout names the primary column
     * `__primary`; on this screen that column is the name.
     */
    function onSort(next) {
        sort.value = next === null ? null : { field: next.key === '__primary' ? 'name' : next.key, direction: next.direction }
        schedule()
    }

    /**
     * What the paginator calls. One-based, like the server's own numbering.
     *
     * The pending keystroke is dropped first. `onQuery` queues `runSearch(1)`,
     * so clicking page two within the debounce window loaded page two and then
     * had it replaced by page one of the same query a moment later.
     */
    function goToPage(nextPage) {
        clearTimeout(timer)
        return runSearch(nextPage)
    }

    /** Read once, on mount. Held here so its refusal reaches the same banner. */
    function loadSurface() {
        return read(() => store.loadSurface())
    }

    // Otherwise a keystroke a quarter-second before navigating away fires a
    // search against the collection somebody has just left.
    onScopeDispose(function () {
        clearTimeout(timer)
    })

    /**
     * What a query may name, read from the collection rather than written here.
     *
     * The server derives it from the same JSON Schema its writes validate
     * against and publishes it on `searchable-surface`; the SDK store reads it
     * once and holds it. vibe-portal hand-writes this object in each list view,
     * which is a second copy of a fact the server owns — the copy that decides
     * what a search bar offers, against the original that decides what the
     * server accepts.
     */
    const fieldDefinitions = computed(() => ({ fields: store.surface ?? {} }))

    /**
     * A readable label per field. Derived, because five collections publish
     * about a hundred fields between them and naming each by hand is a list
     * that goes stale the first time a schema grows a property. `FIELD_META`
     * names the exceptions: a better label, or a field the search UI leaves out.
     *
     * Every enum also carries what each of its values reads as, so the table,
     * the filters and the detail panel say "Past due" rather than `past_due`.
     * And the two timestamps are marked as dates here, because the surface
     * declares them strings: they are stored as ISO text, and a query value
     * coerced to a Date would never match one.
     */
    const fieldMeta = computed(function () {
        const overrides = FIELD_META[collection] ?? {}
        const meta = {}
        for (const [path, declaration] of Object.entries(store.surface ?? {})) {
            const { label = labelFor(path), searchHidden = false, identifier = false } = overrides[path] ?? {}
            const entry = { label }
            if (searchHidden) {
                entry.searchHidden = true
            }
            if (identifier) {
                entry.identifier = true
            }
            if (declaration.type === 'enum') {
                entry.enumMeta = enumMetaFor(path.slice(path.lastIndexOf('.') + 1), declaration.values)
            }
            if (TIMESTAMPS.includes(path)) {
                entry.date = true
            }
            meta[path] = entry
        }
        return meta
    })

    /**
     * The engine encodes the typed prefix as a Mongo range rather than a plain
     * string — there is no `spec.prefix` — so the lower bound is where it is.
     */
    function valueProvider(spec) {
        const prefix = spec.filter?.[spec.field]?.$gte ?? ''
        return store.suggest(spec.field, prefix)
    }

    // The component wants counts beside each value. This server does not count
    // them — `suggest` answers values alone — so `null` says "no count" rather
    // than a zero that would read as "none match".
    function facetProvider({ field }) {
        return store.suggest(field, '').then((values) => values.map((value) => ({ value, count: null })))
    }

    const editable = EDITABLE[collection] ?? ['name']
    const clearable = CLEARABLE[collection] ?? []
    const editableObjects = EDITABLE_OBJECTS[collection] ?? []
    const needsOperation = NEEDS_OPERATION[collection] ?? null

    /**
     * The draft the panel opens on.
     *
     * Without one the shell builds `{}` and the server answers
     * `REQUEST_VALIDATION_FAILED` on the first save, which is a refusal about a
     * form nobody had a chance to fill in.
     *
     * A house's slug is seeded rather than left blank because the pattern is
     * `^[a-z0-9]{4}$` — an empty string fails it, so a blank box could only ever
     * be wrong. Four random characters are a starting point somebody renames; a
     * collision answers `DUPLICATE_UNIQUE_KEY: slug`, which says what to change.
     */
    function newRecord() {
        const base = { name: `New ${singular}` }
        if (collection === 'auction-houses') {
            base.slug = randomSlug()
        }
        return base
    }

    /**
     * Only the fields the route takes, and only the ones that carry a value.
     *
     * `null` is a value here and `undefined` is not: an absent key leaves a
     * property alone, and `null` clears it. A form that never filled `website`
     * must not read as "clear it".
     */
    function patchBody(record) {
        const body = {}
        for (const field of [...editable, ...editableObjects.map((each) => each.key)]) {
            if (record[field] !== undefined) {
                body[field] = valueToSend(field, record[field])
            }
        }
        return body
    }

    /**
     * An emptied box means "clear it" on a field the route lets go, and nothing
     * else anywhere else.
     *
     * A text control answers `''` when somebody deletes what was in it. Sent
     * verbatim to `website` that is a 400 against `format: uri`, and there was
     * no way for the screen to produce the `null` the route wanted.
     */
    function valueToSend(field, value) {
        if (value === '' && clearable.includes(field)) {
            return null
        }
        return value
    }

    /**
     * Both re-read afterwards, and both stop if the write was refused.
     *
     * The stopping is the part that matters. `refresh` clears `error` before it
     * asks — every action does, so a stale refusal never outlives the attempt
     * that caused it — so refreshing after a failed write wipes the very thing
     * the screen was about to show. A refused save reported nothing at all
     * until this returned early.
     */
    async function write(work) {
        writeFailure.value = null
        const ok = await work()
        if (ok === false) {
            // Held here because there is nowhere else for it to go. The banner
            // shows reads only, and in expanded mode the panel covers the
            // banner outright — so a refused Save reported nothing at all, and
            // the button read as dead. The instance body draws this instead.
            writeFailure.value = store.error
            return false
        }
        await read(() => store.refresh())
        return true
    }

    function onSave(record) {
        return write(() => store.patch(record._id, patchBody(record)))
    }

    function onCreate(record) {
        return write(() => store.create(createBody(record)))
    }

    /**
     * A create takes more than a patch does — an Auction House needs the
     * Operation it belongs to before it exists, because a house with none has
     * nothing to resolve a legal identity through and the server refuses it.
     */
    function createBody(record) {
        const body = patchBody(record)
        if (record.operationId !== undefined && record.operationId !== null) {
            body.operationId = record.operationId
        }
        return body
    }

    /**
     * What an empty list says. "No records match this search." showed while the
     * first page was still on its way, and on a collection nobody had added to.
     */
    function renderEmpty() {
        const style = { padding: '50px', textAlign: 'center', color: 'var(--p-text-muted-color)', fontSize: '14px' }
        if (!arrived.value) {
            return h('div', { style, role: 'status' }, [h('i', { class: 'pi pi-spin pi-spinner', 'aria-label': 'Loading' })])
        }
        const text = query.value.trim() === '' ? `No ${title.toLowerCase()} yet.` : 'No records match this search.'
        return h('div', { style }, text)
    }

    const config = computed(() => ({
        storageKey,
        idKey: '_id',
        kindLabel: title,
        primaryLabel: 'Name',
        data: store.documents,
        total: store.totalCount,
        offset: (page.value - 1) * PAGE_SIZE,
        renderEmpty,
        fieldDefinitions: fieldDefinitions.value,
        fieldMeta: fieldMeta.value,
        primary: (record) => record.name || '(unnamed)',
        secondary: (record) => record.slug || '',
        tableColumns: TABLE_COLUMNS[collection] ?? ['__primary', 'status', 'updatedAt'],
        searchPlaceholder: `Search ${title.toLowerCase()} — e.g. status:active name:Sun*`,
        searchHelp: 'Search by field, e.g. status:active — or use Filters.',
        expandOnEdit: true,
        newRecord,
        // Nothing on this server deletes. A dissolved Company is retired and
        // persists, because the documents that named it still say so — so the
        // panel's own lifecycle control replaces the button rather than a
        // Delete that quietly retired instead.
        hideDelete: true,
        onSave,
        onCreate
    }))

    // `editable`, `editableObjects` and `needsOperation` are not config. The
    // panel renders the `#instance` slot INSTEAD of its own field form, so what
    // a body draws is the body's own business — passing it through a config the
    // layout never reads would be one file handing values to itself by way of
    // another.
    return {
        config,
        onQuery,
        onSort,
        loadSurface,
        readFailure,
        writeFailure,
        page,
        pageSize: PAGE_SIZE,
        goToPage,
        valueProvider,
        facetProvider,
        editable,
        editableObjects,
        needsOperation
    }
}

/**
 * Four characters from the slug alphabet.
 *
 * `Math.random` rather than anything ordered, because the only requirement is
 * that two people creating a house at once rarely collide — and when they do,
 * the server says so by name.
 */
function randomSlug() {
    const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789'
    let slug = ''
    while (slug.length < 4) {
        slug = slug + alphabet[Math.floor(Math.random() * alphabet.length)]
    }
    return slug
}
